import { type SupplierListItem } from '@choco-admin/types';
import { Card, Skeleton, Switch } from '@heroui/react';
import React, { useState } from 'react';

import { AlertCard } from '../../../../../../components/AlertCard';
import { useNotification } from '../../../../../../contexts/NotificationContext';

import { DisableOrderAgentModal } from './DisableOrderAgentModal';
import { useOrderAgentSetting } from './hooks/useOrderAgentSetting';

interface OrderAgentTabProps {
  supplier?: SupplierListItem;
}

/**
 * The distributor's Order Agent switch. It saves on its own, outside the form's
 * Save button: turning the agent off changes live order processing, so it is
 * confirmed and applied as one deliberate action rather than batched with
 * profile edits.
 */
export const OrderAgentTab: React.FC<OrderAgentTabProps> = ({ supplier }) => {
  const { addNotification } = useNotification();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { isEnabled, isLoading, errorMessage, setEnabled, isSaving } =
    useOrderAgentSetting(supplier?.id);

  if (!supplier) {
    return (
      <AlertCard
        type="info"
        title="Create distributor first"
        message="Please save the distributor to manage its Order Agent."
      />
    );
  }

  const supplierName = supplier.name || 'This distributor';

  const save = async (enabled: boolean) => {
    const result = await setEnabled(enabled);
    setIsConfirmOpen(false);

    if (result.kind === 'error') {
      addNotification({
        title: 'Order Agent not changed',
        message: result.message,
        type: 'error',
      });
      return;
    }

    addNotification({
      title: enabled ? 'Order Agent enabled' : 'Order Agent disabled',
      message: `${supplierName} ${enabled ? 'now has' : 'no longer has'} the Order Agent.`,
      type: 'success',
    });
  };

  const handleToggle = (next: boolean) => {
    if (next) {
      save(true);
    } else {
      setIsConfirmOpen(true);
    }
  };

  return (
    <Card className="p-8 rounded-xl">
      <div className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-1">Order Agent</h3>
            <p className="text-sm text-default-500">
              When enabled, Choco AI processes the orders this distributor
              receives by email, WhatsApp, SMS and upload.
            </p>
          </div>
          {isLoading ? (
            <Skeleton className="h-7 w-12 rounded-full" />
          ) : (
            <Switch
              aria-label="Order Agent enabled"
              isSelected={isEnabled}
              isDisabled={Boolean(errorMessage) || isSaving}
              onValueChange={handleToggle}
            >
              {isEnabled ? 'Enabled' : 'Disabled'}
            </Switch>
          )}
        </div>

        {errorMessage && (
          <AlertCard
            type="error"
            title="Could not load the Order Agent"
            message={errorMessage}
          />
        )}
      </div>

      <DisableOrderAgentModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => save(false)}
        supplierName={supplier.name || 'this distributor'}
        isDisabling={isSaving}
      />
    </Card>
  );
};
